import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/core/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// icons
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";

import "react-perfect-scrollbar/dist/css/styles.css";

import { PageFooter, Button, translate, showNotification } from "jazasoft";

import Table, { NativeTextInput } from "../../../components/Table";

import { defaultParsePaste, getDistinctValues, isEmpty, toFixed } from "../../../utils/helpers";
import { useDeepEffect, useDeepMemo, useMutation } from "../../../hooks";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { REGEX_POSITIVE_NUMBER } from "../../../utils/regex";
import { DEFAULT_SEPARATOR } from "../../../constants";
import { Divider } from "@material-ui/core";

const NON_SB_FIELDS = ["styleColor", "style", "color", "info", "totalPercent", "total"];

const useStyles = makeStyles(theme => ({
  drawer: ({ drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0
  }),
  drawerPaper: ({ drawerWidth }) => ({
    width: drawerWidth
  }),
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    margin: "1.5em"
  },
  content: {
    // padding: "0 1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
}));

const FPOBreakupDrawer = props => {
  const { translate, open, action, fpoOrder, layList, onDrawerClose, onRefresh } = props;
  const classes = useStyles({ drawerWidth: 1080 });

  const [breakupRows, setBreakupRows] = useState([]);
  const [editing, setEditing] = useState(false);

  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    setEditing(open && action === "update");
  }, [open, action]);

  const flowInfoList = fpoOrder?.flowInfoList || [];

  useEffect(() => {
    setBreakupRows([]);
  }, [open]);

  const onBreakupChange =
    ({ rowIdx, record, column }) =>
    e => {
      const value = e?.target?.value || e?.value;

      setBreakupRows(prev => {
        let newRows = [...prev];
        let row;
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          // Restricting input of String value in size rows
          let allowInput = !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value);
          if (allowInput) {
            if (column.dataKey === "totalPercent") {
              const fpoQtyRow = prev.find(e => e.style === record.style && e.color === record.color && e.info === "FPO Qty");
              const bundleQty = Object.keys(fpoQtyRow)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => ({ ...acc, [key]: Math.round(value * fpoQtyRow[key] * 0.01) }), {});
              row = { ...prev[rowIdx], ...bundleQty, [column.dataKey]: value };
              const total = Object.keys(row)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => acc + (isEmpty(row[key]) ? 0 : Number(row[key])), 0);
              newRows.splice(rowIdx, 1, { ...row, total });
            } else {
              row = { ...prev[rowIdx], [column.dataKey]: value };
              const total = Object.keys(row)
                .filter(key => !NON_SB_FIELDS.includes(key))
                .reduce((acc, key) => acc + (isEmpty(row[key]) ? 0 : Number(row[key])), 0);
              newRows.splice(rowIdx, 1, { ...row, total });
            }
          }
        }

        return [...newRows];
      });
    };

  //** Prepare Size Breakup Columns**/
  const { columns, headerRows, sizeKeys } = useDeepMemo(() => {
    if (isEmpty(flowInfoList)) return { columns: [], headerRows: [] };

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || []);
    const styleList = getDistinctValues(flowInfoList.map(fi => fi.style));
    const colorList = getDistinctValues(flowInfoList.map(fi => fi.color));

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    const editable = action !== "view";

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }

      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        leftBorder,
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column }) =>
              !editable ||
              ["Order Qty", "FPO Qty", "Synced Cut Qty"].includes(record.info) ||
              column.dataKey === "total" ||
              column.dataKey === "styleColor"
            }
          />
        )
      });
    }

    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let columns = [
      {
        dataKey: "style",
        title: "Style",
        leftBorder: true,
        rowSpan: ({ rowIdx }) => (rowIdx % 4 === 0 ? 4 : 0)
      },
      {
        dataKey: "color",
        title: "Color",
        leftBorder: true,
        rowSpan: ({ rowIdx }) => (rowIdx % 4 === 0 ? 4 : 0)
      },
      {
        dataKey: "info",
        title: "Info",
        leftBorder: true
      },
      {
        dataKey: "totalPercent",
        title: "Total (%)",
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column }) => !editable || ["Order Qty", "FPO Qty", "Synced Cut Qty"].includes(record.info)}
          />
        )
      }
    ];
    if (styleList.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "style");
    }
    if (colorList.length <= 1) {
      columns = columns.filter(c => c.dataKey !== "color");
    }

    columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }
    return { columns, headerRows, sizeKeys };
  }, [flowInfoList, action, fpoOrder, editing]);

  //** Prepare Size Breakup Rows **/
  useDeepEffect(() => {
    if (isEmpty(flowInfoList) || isEmpty(fpoOrder)) return;

    const emptyQty = sizeKeys.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: 0 }), {});

    const styleColorList = getDistinctValues(flowInfoList.map(fi => fi.styleColor));
    const rowList = styleColorList.flatMap(styleColor => {
      const finalFlowInfoList = flowInfoList.filter(e => e.styleColor === styleColor);
      const [style, color] = styleColor.split(DEFAULT_SEPARATOR);

      const sizeBreakupList = finalFlowInfoList.flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || []);
      const fpoSizeBreakupList = finalFlowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);

      const totalOrderQty = sizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const orderQty = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);

      const totalFpoQty = fpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const fpoQty = fpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);

      const reqFpoSizeBreakupList = fpoOrder.fpoSizeBreakupList?.filter(sb => sb.styleColor === styleColor) || [];
      const totalReqFpoQty = reqFpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const reqFpoQty = reqFpoSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);

      const cutSizeBreakupList = layList
        .filter(lay => {
          const externalIds = !isEmpty(lay.externalIds) ? JSON.parse(lay.externalIds) : [];
          return externalIds.some(e => e.syncedVersion > 0);
        })
        .flatMap(lay => {
          const breakupList = !isEmpty(lay.fpoBreakup) ? JSON.parse(lay.fpoBreakup) : [];
          return breakupList.find(e => e.externalFpo === fpoOrder.fpo)?.sizeBreakupList || [];
        })
        .filter(sb => sb.styleColor === styleColor);
      const totalSyncedCutQty = cutSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const syncedCutQty = cutSizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: (acc[sb.sizeKey] || 0) + sb.qty }), emptyQty);

      let rows = [
        {
          style,
          color,
          info: "Order Qty",
          total: totalOrderQty,
          totalPercent: 100,
          ...orderQty
        },
        {
          style,
          color,
          info: "FPO Qty",
          total: totalFpoQty,
          totalPercent: 100,
          ...fpoQty
        },
        {
          style,
          color,
          info: "Req. FPO Qty",
          total: totalReqFpoQty,
          totalPercent: totalFpoQty > 0 ? toFixed((totalReqFpoQty * 100) / totalFpoQty) : 0,
          ...reqFpoQty
        },
        {
          style,
          color,
          info: "Synced Cut Qty",
          total: totalSyncedCutQty,
          totalPercent: totalFpoQty > 0 ? toFixed((totalSyncedCutQty * 100) / totalFpoQty) : 0,
          ...syncedCutQty
        }
      ];

      return rows;
    });

    setBreakupRows(rowList);
  }, [flowInfoList, fpoOrder?.fpoSizeBreakupList, layList, editing, sizeKeys]);

  const onSubmit = () => {
    const styleColorList = getDistinctValues(flowInfoList.map(fi => fi.styleColor));
    const sizeBreakupList = styleColorList.flatMap(styleColor => {
      const [style, color] = styleColor.split(DEFAULT_SEPARATOR);
      const reqFpoQty = breakupRows.find(row => row.style === style && row.color === color && row.info === "Req. FPO Qty") || {};

      const sbList = columns
        .filter(c => !NON_SB_FIELDS.includes(c.dataKey))
        .map((c, idx) => ({
          serialNo: idx + 1,
          type: "FPO_ORDER",
          style,
          color,
          size: c.dataKey.substring(c.dataKey.indexOf("_") + 1),
          sizeGroup: c.dataKey.substring(0, c.dataKey.indexOf("_")),
          sizeKey: c.dataKey,
          qty: reqFpoQty[c.dataKey] || 0
        }));
      return sbList;
    });

    const options = {
      url: `order-flow/${fpoOrder.id}/fpo-breakup`,
      method: "put",
      data: sizeBreakupList
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Required FPO Qty updated successfully."));
        onRefresh && onRefresh();
        onDrawerClose();
      }
    });
  };

  return (
    <MuiDrawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      classes={{
        paper: classes.drawerPaper
      }}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>

        <Typography style={{ marginLeft: "0.5em" }} variant="h5">
          Bundle Required Qty
        </Typography>
      </div>
      <Divider />
      <div className={classes.content}>
        <div>
          <Table
            classes={{ table: classes.table, rowCell: classes.rowCell }}
            columns={columns}
            headerRows={headerRows}
            rows={breakupRows}
            i18nKey="resources.orders.fields.flowInfo"
            headerCellStyle={column => ({
              width: NON_SB_FIELDS.includes(column?.dataKey) ? 100 : undefined,
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              fontWeight: "bold",
              backgroundColor: "#C6D0DA",
              textAlign: column.dataKey === "info" ? "left" : "center"
            })}
            cellStyle={({ column, record }) => ({
              fontWeight: record.isTotalRow ? "bold" : "normal",
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              textAlign: column.dataKey === "info" ? "left" : "center",
              paddingLeft: column.dataKey === "info" ? 8 : 0
            })}
          />

          {/* Footer */}
          <PageFooter className={classes.pageFooter}>
            {editing && (
              <Button
                style={{ marginLeft: "1em" }}
                label={translate("resources.orders.action.save", { _: "SAVE" })}
                color="primary"
                variant="contained"
                disabled={saving}
                onClick={onSubmit}>
                <SaveIcon />
                {saving && <CircularProgress size={20} thickness={2} />}
              </Button>
            )}
            {editing && (
              <Button
                style={{ marginLeft: "1em" }}
                label={translate("resources.orders.action.cancel", { _: "CANCEL" })}
                color="primary"
                variant="outlined"
                disabled={saving}
                onClick={onDrawerClose}>
                <CancelIcon />
              </Button>
            )}
          </PageFooter>
        </div>
      </div>
    </MuiDrawer>
  );
};

export default translate(FPOBreakupDrawer);
